/**
 * Settlement minimap — north-locked, derived from `WorldData`.
 *
 * The static geometry is baked ONCE into an offscreen canvas (see
 * `render/minimap-draw.ts`) and thereafter only blitted, so a frame costs one
 * `drawImage` plus the player marker rather than re-rasterising every rotated
 * building. Redraws are throttled: a minimap does not need 60Hz.
 *
 * If `world.minimapImageUrl` is set, that picture is used as the base layer
 * instead of the derived bake — the "bake a picture, ship it as an asset" path.
 * The live player marker is drawn on top either way, so a baked image and a
 * derived base behave identically from here. If the image fails to load, the
 * derived bake is used, so a bad path degrades to a working minimap.
 *
 * Dev-only: `bakeToDownload()` saves the current derived base as a PNG. In DEV
 * builds it is reachable from the console as `window.__bakeMinimap()`.
 */
import type { Vec2, WorldData } from '../types';
import {
  bakeMinimapCanvas, bakeMinimapDataUrl, drawPlayerMarker, drawViewCone, MINIMAP_BAKE_SIZE, projectToMinimap,
} from '../render/minimap-draw';

const REDRAW_MS = 60; // ~16Hz — smooth enough for a 180px map, ~4x cheaper than per-frame

interface DevWindow extends Window {
  __bakeMinimap?: () => void;
}

export class Minimap {
  readonly el: HTMLDivElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly world: WorldData;
  private base: HTMLCanvasElement | HTMLImageElement | null;
  private readonly ro: ResizeObserver;
  private lastDraw = 0;
  private cssSize = 0;
  private pins: { x: number; z: number }[] = [];

  constructor(parent: HTMLElement, world: WorldData) {
    this.world = world;
    this.el = document.createElement('div');
    this.el.className = 'bv-minimap';
    this.el.innerHTML = `<div class="bv-minimap-north">N</div>`;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'bv-minimap-canvas';
    this.el.appendChild(this.canvas);
    parent.appendChild(this.el);

    this.ctx = this.canvas.getContext('2d');
    this.base = bakeMinimapCanvas(world);

    if (world.minimapImageUrl) this.loadBaseImage(world.minimapImageUrl);

    this.ro = new ResizeObserver(() => this.resize());
    this.ro.observe(this.el);
    this.resize();

    if (import.meta.env.DEV) {
      (window as DevWindow).__bakeMinimap = () => this.bakeToDownload();
    }
  }

  /**
   * Called from the frame loop. Self-throttled, and skips entirely while
   * hidden — a minimap behind a full-screen panel is pure waste.
   */
  update(now: number, position: Vec2, facing: number, cameraYaw: number): void {
    if (this.el.hidden) return;
    if (now - this.lastDraw < REDRAW_MS) return;
    this.lastDraw = now;
    this.draw(position, facing, cameraYaw);
  }

  /** Quest place-markers drawn as pulsing rings (the farm pump, ...). */
  setPins(pins: readonly { x: number; z: number }[]): void {
    this.pins = pins.map((p) => ({ x: p.x, z: p.z }));
    this.lastDraw = 0;
  }

  /** Save the derived base layer as a PNG. Dev tool — see the class docstring. */
  bakeToDownload(size = MINIMAP_BAKE_SIZE): void {
    const url = bakeMinimapDataUrl(this.world, size);
    if (!url) return;
    const a = document.createElement('a');
    a.href = url;
    a.download = `minimap-${this.world.cityName.toLowerCase().replace(/\s+/g, '-')}.png`;
    a.click();
  }

  private loadBaseImage(url: string): void {
    const img = new Image();
    // Only swap the base once it has actually decoded — assigning it up front
    // would blank the minimap until the fetch lands, and permanently on a 404.
    img.onload = () => {
      this.base = img;
      this.lastDraw = 0;
    };
    img.onerror = () => {
      console.warn('[minimap] minimapImageUrl failed to load, using the derived bake:', url);
    };
    img.src = url;
  }

  private resize(): void {
    const css = this.el.clientWidth || 0;
    if (!css) return;
    this.cssSize = css;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.round(css * dpr);
    this.canvas.height = Math.round(css * dpr);
    this.lastDraw = 0; // force the next update() to redraw at the new size
  }

  private draw(position: Vec2, facing: number, cameraYaw: number): void {
    const { ctx } = this;
    if (!ctx || !this.cssSize) return;
    const size = this.canvas.width;

    ctx.clearRect(0, 0, size, size);
    if (this.base) ctx.drawImage(this.base, 0, 0, size, size);

    // Under the marker, so the marker stays readable on top of it.
    drawViewCone(ctx, this.world, size, position, cameraYaw, size * 0.32);
    const pulse = (Math.sin(performance.now() / 260) + 1) / 2;
    for (const pin of this.pins) {
      const p = projectToMinimap(pin.x, pin.z, this.world.ground, size);
      const r = Math.max(4, size / 45);
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * (1.2 + pulse * 0.9), 0, Math.PI * 2);
      ctx.lineWidth = Math.max(1.5, size / 140);
      ctx.strokeStyle = `rgba(217,83,46,${0.9 - pulse * 0.55})`;
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
      ctx.fillStyle = '#d9532e';
      ctx.fill();
      ctx.lineWidth = Math.max(1, size / 200);
      ctx.strokeStyle = '#15120c';
      ctx.stroke();
    }
    drawPlayerMarker(ctx, this.world, size, position, facing);
  }

  dispose(): void {
    this.ro.disconnect();
    if (import.meta.env.DEV) delete (window as DevWindow).__bakeMinimap;
    this.el.remove();
  }
}
